import {
  Container,
  Flex,
  Paper,
  Text,
  Badge,
  Tabs,
  Anchor,
  Title,
  Box,
  Button,
  Center,
  Loader,
  Group,
  Modal,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconRosetteDiscountCheckFilled,
  IconWorld,
  IconExternalLink,
  IconArrowLeft,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconPencil,
} from "@tabler/icons-react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { normalizeIndianGstinInput } from "@shared/indianGstin";
import trpc from "@/shared/utils/trpc";
import {
  getTrpcErrorMessage,
  handleTrpcError,
  isIndiaGridlinesUserVerificationError,
} from "@/shared/utils/trpc/handletrpcError.util";
import { getFileUrl } from "@/shared/utils/useFileUrl";
import { formatCompanyDisplayName } from "@/shared/utils/companyDisplayName.util";
import {
  getCompanyWebsiteDisplayLabel,
  hasCompanyWebsite,
} from "@/shared/utils/companyWebsiteDisplay.util";
import { notifications } from "@mantine/notifications";
import MatchedProfileTab from "@/pages/profile/MatchedPrfofielTab";
import VerificationStatusBadge from "../../AdminStausBadge";
import AdminCollaborationsTab from "./CollaborationTab";
import AdminInterestsTab from "./InterestRequestTab";
import AdminCreditHistoryTab from "./CreditHistoryTab";
import AdminSignupLegalAcceptanceTab from "./AdminSignupLegalAcceptanceTab";
import AdminCompanyAuthorityAcceptanceTab from "./AdminCompanyAuthorityAcceptanceTab";
import VerifyCompanyModal from "./VerifyModal";
import ApplyVerificationModal from "@/pages/profile-verification/ApplyVerificationModal";
import { successNotification } from "@/shared/utils/successNotification";
import RejectCompanyModal from "./RejectModal";
import { formatHeadquartersLocationLine } from "@/shared/constants/signup.const";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TAB_LABEL_FZ,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navyProfileNavTabBorderRadius,
} from "@/shared/styles/navTabsLayout";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";

export default function AdminCompanyDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>("profile");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [editCompanyOpened, setEditCompanyOpened] = useState(false);
  const [editContactOpened, setEditContactOpened] = useState(false);
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  const [savingContactEdit, setSavingContactEdit] = useState(false);

  // Admin step-up 2FA (required on user.updateProfile when admin has 2FA enabled)
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [twoFactorOtp, setTwoFactorOtp] = useState("");
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const pendingTwoFactorContactEditRef = useRef<
    | null
    | {
        userId: string;
        name?: string;
        designation?: string;
        email?: string;
        phoneNumber?: string;
      }
  >(null);
  const [companyEditForm, setCompanyEditForm] = useState({
    legalName: "",
    tradeName: "",
    headquartersCity: "",
    headquartersCountry: "",
    email: "",
    phoneNumber: "",
    website: "",
    description: "",
  });
  const [contactEditForm, setContactEditForm] = useState({
    name: "",
    designation: "",
    email: "",
    phoneNumber: "",
  });

  const [verifyModalOpened, { open: openVerifyModal, close: closeVerifyModal }] = useDisclosure(false);
  const [registryVerifyModalOpened, { open: openRegistryVerifyModal, close: closeRegistryVerifyModal }] =
    useDisclosure(false);
  const [registryVerifyLoading, setRegistryVerifyLoading] = useState(false);
  const [registryVerificationData, setRegistryVerificationData] = useState<Record<string, unknown> | null>(null);
  const [registryVerificationSubmitError, setRegistryVerificationSubmitError] = useState<string | null>(null);
  const [verifyDirectlyModalOpened, { open: openVerifyDirectlyModal, close: closeVerifyDirectlyModal }] = useDisclosure(false);
  const [rejectModalOpened, { open: openRejectModal, close: closeRejectModal }] = useDisclosure(false);
  const [disableModalOpened, { open: openDisableModal, close: closeDisableModal }] = useDisclosure(false);

  const {
    isMobile: isAdminCompanyNavMobile,
    tabsListStyles: adminCompanyNavTabsListStyles,
    tabsRootStyles: adminCompanyNavTabsRootStyles,
  } = useNavTabsStackLayout(7, {
    width: "100%",
    maxWidth: "100%",
    gap: 0,
    alignItems: "stretch",
  });

  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  const fetchCompanyData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const companyData = await trpc.admin.getById.query({ companyId: id });
      setCompany(companyData);
    } catch (err) {
      console.error("Error fetching company:", err);
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchCompanyData();
  }, [fetchCompanyData]);

  // Fetch logo URL from S3 when company changes
  useEffect(() => {
    if (company?.logo?.id) {
      getFileUrl(company.logo.id).then((url) => {
        setLogoUrl(url);
      });
    }
  }, [company]);

  const handleVerify = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const result = await trpc.admin.companyApprove.mutate({ companyId: id });

      setCompany((prev: any) => ({
        ...prev,
        status: result.status,
        adminComments: result.adminComments,
      }));

      closeVerifyModal();
      await fetchCompanyData();

      successNotification({
        title: "Profile Verified",
        message: "Company profile has been verified",
      });
    } catch (err: any) {
      console.error("Error verifying company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const openRegistryVerificationModal = () => {
    setRegistryVerificationData(null);
    openRegistryVerifyModal();
  };

  const closeRegistryVerificationModal = () => {
    closeRegistryVerifyModal();
    setRegistryVerificationData(null);
    setRegistryVerificationSubmitError(null);
  };

  const handleApplyRegistryVerification = async (payload: { registrationIdType: string; registrationNumber: string }) => {
    if (!id || !company) return;

    const hqTrim = (company.headquartersCountry || "")?.trim() ?? "";
    const usesLei = payload.registrationIdType === "LEI";
    const usesUsccShape =
      payload.registrationIdType === "USCC" ||
      (payload.registrationIdType === "COUNTRY_REGISTRATION" && hqTrim === "China (Mainland)");
    const usesGst = payload.registrationIdType === "GST";
    const usesDigitsOnlyType = payload.registrationIdType === "NOGN" || payload.registrationIdType === "SIREN";
    const normalizedRegistrationNumber =
      usesLei || usesUsccShape
        ? payload.registrationNumber.trim().toUpperCase()
        : usesGst
          ? normalizeIndianGstinInput(payload.registrationNumber)
          : usesDigitsOnlyType
            ? payload.registrationNumber.replace(/\D/g, "")
            : payload.registrationNumber.trim();

    const hqCountry = (company.headquartersCountry || "").trim();
    setRegistryVerificationSubmitError(null);
    setRegistryVerifyLoading(true);
    try {
      await trpc.company.update.mutate({
        companyId: id,
        data: {
          registrationIdType: payload.registrationIdType,
          registrationNumber: normalizedRegistrationNumber || null,
        },
      });

      const result = await trpc.admin.verifyCompanyApi.mutate({ companyId: id });

      await fetchCompanyData();

      if (result && typeof result === "object" && "success" in result && result.success === false) {
        successNotification({
          title: "Verification pending",
          message: (result as { message?: string }).message || "Verification remains pending admin review.",
        });
        closeRegistryVerificationModal();
        return;
      }

      if (result && typeof result === "object" && "success" in result && result.success && "data" in result && result.data) {
        const data = result.data as Record<string, unknown>;
        setRegistryVerificationData({
          ...data,
          _verificationSource: (data.verificationSource as string | undefined) ?? (result as { source?: string }).source,
        });
        successNotification({
          title: "API verification completed",
          message: "Company verification has been run successfully.",
        });
        return;
      }

      successNotification({
        title: "API verification completed",
        message: "Company verification API has been run successfully.",
      });
      closeRegistryVerificationModal();
    } catch (err: any) {
      console.error("Error running registry verification:", err);
      handleTrpcError(err);
      closeRegistryVerificationModal();
    } finally {
      setRegistryVerifyLoading(false);
    }
  };

  const handleVerifyDirectly = async () => {
    if (!id) return;

    setActionLoading(true);
    try {
      const result = await trpc.admin.companyApprove.mutate({ companyId: id });

      setCompany((prev: any) => ({
        ...prev,
        status: result.status,
        adminComments: result.adminComments,
      }));

      closeVerifyDirectlyModal();

      successNotification({ title: "Profile Verified", message: "Company profile has been verified" });
    } catch (err: any) {
      console.error("Error verifying company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!id) return;
    setActionLoading(true);
    try {
      await trpc.admin.companyReject.mutate({ companyId: id, adminComments: reason });
      setCompany((prev: any) => ({ ...prev, status: "ACTION_REQUIRED", adminComments: reason }));
      closeRejectModal();
      successNotification({ title: "Profile Rejected", message: "Company profile has been rejected" });
    } catch (err) {
      console.error("Error rejecting company:", err);
      handleTrpcError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!id) return;

    if (!company?.adminBlocked) {
      // Open modal for disable
      openDisableModal();
      return;
    }

    // Enable directly
    setBlockLoading(true);
    try {
      await trpc.admin.unblockCompany.mutate({ companyId: id });
      setCompany((prev: any) => ({ ...prev, adminBlocked: false }));
      successNotification({ title: "Profile Enabled", message: "Company profile has been enabled" });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleConfirmDisable = async (reason: string) => {
    if (!id) return;
    setBlockLoading(true);
    try {
      await trpc.admin.blockCompany.mutate({ companyId: id, reason });
      setCompany((prev: any) => ({ ...prev, adminBlocked: true }));
      closeDisableModal();
      successNotification({ title: "Profile Disabled", message: "Company profile has been disabled" });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setBlockLoading(false);
    }
  };

  const openEditCompanyModal = () => {
    if (!company) return;
    setCompanyEditForm({
      legalName: company.legalName || "",
      tradeName: company.tradeName || "",
      headquartersCity: company.headquartersCity || "",
      headquartersCountry: company.headquartersCountry || "",
      email: company.email || "",
      phoneNumber: company.phoneNumber || "",
      website: company.website || "",
      description: company.description || "",
    });
    setEditCompanyOpened(true);
  };

  const openEditContactModal = () => {
    if (!company?.user) return;
    setContactEditForm({
      name: company.user.name || "",
      designation: company.user.designation || "",
      email: company.user.email || "",
      phoneNumber: company.user.phoneNumber || "",
    });
    setEditContactOpened(true);
  };

  const saveCompanyEdit = async () => {
    if (!id) return;
    setSavingCompanyEdit(true);
    try {
      await trpc.company.update.mutate({
        companyId: id,
        data: {
          legalName: companyEditForm.legalName.trim() || null,
          tradeName: companyEditForm.tradeName.trim() || null,
          headquartersCity: companyEditForm.headquartersCity.trim() || null,
          headquartersCountry: companyEditForm.headquartersCountry.trim() || null,
          email: companyEditForm.email.trim() || null,
          phoneNumber: companyEditForm.phoneNumber.trim() || null,
          website: companyEditForm.website.trim() || null,
          description: companyEditForm.description.trim() || null,
        },
      });
      setEditCompanyOpened(false);
      await fetchCompanyData();
      successNotification({ title: "Company updated", message: "Company details were updated successfully." });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSavingCompanyEdit(false);
    }
  };

  const saveContactEdit = async () => {
    if (!company?.user?.id) return;
    const userId = company.user.id;
    setSavingContactEdit(true);
    try {
      await trpc.user.updateProfile.mutate({
        userId,
        name: contactEditForm.name.trim() || undefined,
        designation: contactEditForm.designation.trim() || undefined,
        email: contactEditForm.email.trim() || undefined,
        phoneNumber: contactEditForm.phoneNumber.trim() || undefined,
      });
      setEditContactOpened(false);
      await fetchCompanyData();
      successNotification({ title: "Contact updated", message: "User contact details were updated successfully." });
    } catch (err) {
      const trpcMessage =
        (err as any)?.error?.json?.message ||
        (err as any)?.data?.json?.message ||
        (err as any)?.shape?.message ||
        (err as any)?.message ||
        "";

      if (typeof trpcMessage === "string" && trpcMessage.includes("2FA_REQUIRED")) {
        pendingTwoFactorContactEditRef.current = {
          userId,
          name: contactEditForm.name.trim() || undefined,
          designation: contactEditForm.designation.trim() || undefined,
          email: contactEditForm.email.trim() || undefined,
          phoneNumber: contactEditForm.phoneNumber.trim() || undefined,
        };
        setTwoFactorOtp("");
        // Close the edit modal so the OTP modal is guaranteed visible.
        setEditContactOpened(false);
        setTwoFactorOpen(true);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setSavingContactEdit(false);
    }
  };

  const confirmTwoFactorForContactEdit = async () => {
    const pending = pendingTwoFactorContactEditRef.current;
    if (!pending) {
      setTwoFactorOpen(false);
      return;
    }

    const otp = twoFactorOtp.trim();
    if (!otp) {
      notifications.show({ title: "2FA required", message: "Enter your authenticator code.", color: "red" });
      return;
    }

    setTwoFactorBusy(true);
    try {
      await trpc.user.updateProfile.mutate({
        userId: pending.userId,
        name: pending.name,
        designation: pending.designation,
        email: pending.email,
        phoneNumber: pending.phoneNumber,
        otp,
      });

      pendingTwoFactorContactEditRef.current = null;
      setTwoFactorOpen(false);
      setEditContactOpened(false);
      await fetchCompanyData();
      successNotification({ title: "Contact updated", message: "User contact details were updated successfully." });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const showFinalApproveVerifyButton = company?.status === "SYSTEM_VERIFIED";
  const showRegistryVerifyButton =
    company?.status !== "VERIFIED" && company?.status !== "SYSTEM_VERIFIED";
  const showRejectButton = company?.status === "SYSTEM_VERIFIED" || company?.status === "UNDER_REVIEW";
  const showVerifyDirectlyButton = company?.status === "DRAFT";

  const getAdminCompanyTabStyle = (tabValue: string, slot: "first" | "middle" | "last") => ({
    ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
    backgroundColor: activeTab === tabValue ? "#04305c" : "#ebf0ff",
    color: activeTab === tabValue ? "white" : "var(--mantine-color-slate-6)",
    borderRadius: navyProfileNavTabBorderRadius(isAdminCompanyNavMobile, slot),
    border: activeTab === tabValue ? "1px solid #04305c" : "1px solid #E5E7EB",
    borderBottom: "none",
    fontWeight: activeTab === tabValue ? 500 : 400,
    cursor: "pointer",
    minHeight: 44,
    height: "100%",
  });

  if (loading) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  if (!company) {
    return (
      <Container size="fluid" py="xl">
        <Text>Company not found</Text>
      </Container>
    );
  }

  return (
    <Box>
      {/* Back Button */}
      <Box py="sm" px="xl">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          size="sm"
          onClick={() => navigate("/admin/companies")}
        >
          Back to Companies
        </Button>
      </Box>

      <Container size="fluid" px="xl" pb="xs">
        {/* Company Header */}
        <Paper
          p="xl"
          radius="lg"
          mb="xl"
          style={{
            border: "1px solid #E5E7EB",
            backgroundColor: "#fff",
          }}
        >
          <Flex justify="space-between" align="flex-start" gap="md" direction={{ base: "column", sm: "row" }} wrap="wrap">
            <Flex gap="md" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
              <Flex
                w={64}
                h={64}
                style={{
                  borderRadius: 8,
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#F9FAFB",
                  flexShrink: 0,
                }}
                align="center"
                justify="center"
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                  />
                ) : (
                  <IconBuilding size={28} color="#6B7280" />
                )}
              </Flex>

              <Flex direction="column" gap={4}>
                {/* Row 1: Company Name + Verified Icon */}
                <Flex align="center" gap="xs">
                  <Title order={3} fw={500}>
                    {formatCompanyDisplayName(company.legalName, company.tradeName)}
                  </Title>
                  {company.status === "VERIFIED" && (
                    <IconRosetteDiscountCheckFilled size={20} style={{ color: "var(--mantine-color-primary-6)" }} />
                  )}
                </Flex>

                {/* Row 2: GTID Badge */}
                {company.gtid && (
                  <Badge
                    variant="light"
                    size="sm"
                    radius="sm"
                    style={{
                      width: "fit-content",
                      backgroundColor: "#EEF2F7",
                      color: "#374151",
                    }}
                  >
                    {company.gtid}
                  </Badge>
                )}

                {/* Row 3: Location, Phone, Email */}
                <Flex gap="md" align="center" mt={4} wrap="wrap">
                  <Flex align="center" gap={6}>
                    {headquartersFlagEmoji ? (
                      <Text
                        component="span"
                        fz={18}
                        lh={1}
                        role="img"
                        aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                      >
                        {headquartersFlagEmoji}
                      </Text>
                    ) : null}
                    <Text size="sm" c="slate.6">
                      {formatHeadquartersLocationLine(company.headquartersCity, company.headquartersCountry)}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={6}>
                    <IconPhone size={16} color="#6B7280" />
                    <Text size="sm" c="slate.6">
                      {company.phoneCountryCode && company.phoneNumber
                        ? `${company.phoneCountryCode} ${company.phoneNumber}`
                        : "N/A"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={6}>
                    <IconMail size={16} color="#6B7280" />
                    <Text size="sm" c="slate.6">
                      {company.email || "N/A"}
                    </Text>
                  </Flex>
                </Flex>

                {/* Row 4: Website */}
                <Flex gap="md" align="center" mt={2} wrap="wrap">
                  <Flex align="center" gap={6} miw={0} style={{ maxWidth: "100%" }}>
                    <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                    {hasCompanyWebsite(company.website) ? (
                      <Anchor href={company.website!.trim()} target="_blank" td="none" style={{ minWidth: 0 }}>
                        <Flex align="center" gap={6} wrap="wrap">
                          <Text size="sm" c="primary.6" style={{ wordBreak: "break-all" }}>
                            {getCompanyWebsiteDisplayLabel(company.website)}
                          </Text>
                          <IconExternalLink size={14} color="var(--mantine-color-primary-6)" />
                        </Flex>
                      </Anchor>
                    ) : (
                      <Text size="sm" c="slate.7">
                        {getCompanyWebsiteDisplayLabel(company.website)}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            {/* Right side: Verification Status Badge + Action Buttons */}
            <Flex direction="column" align={{ base: "flex-start", sm: "flex-end" }} gap="sm">
              {company.status !== "VERIFIED" && <VerificationStatusBadge status={company.status} />}

              {(showFinalApproveVerifyButton || showRegistryVerifyButton || showRejectButton) && (
                <Group gap="sm" justify="flex-end">
                  {showRegistryVerifyButton && (
                    <Button
                      variant="filled"
                      color="primary.6"
                      size="sm"
                      radius="md"
                      leftSection={<IconRosetteDiscountCheckFilled size={16} />}
                      onClick={openRegistryVerificationModal}
                      disabled={actionLoading || registryVerifyLoading}
                    >
                      Verify Company
                    </Button>
                  )}
                  {showFinalApproveVerifyButton && (
                    <Button
                      variant="filled"
                      color="primary.6"
                      size="sm"
                      radius="md"
                      leftSection={<IconCheck size={16} />}
                      onClick={openVerifyModal}
                      disabled={actionLoading}
                    >
                      Verify
                    </Button>
                  )}
                  {showRejectButton && (
                    <Button
                      variant="outline"
                      color="red"
                      size="sm"
                      radius="md"
                      leftSection={<IconX size={16} />}
                      onClick={openRejectModal}
                      disabled={actionLoading}
                    >
                      Reject
                    </Button>
                  )}
                </Group>
              )}
              <Group gap="sm">
                <Button
                  variant="filled"
                  color="primary.6"
                  size="sm"
                  leftSection={<IconPencil size={16} />}
                  onClick={() => navigate(`/admin/companies/${id}/edit`)}
                >
                  Edit Full Profile
                </Button>
                <Button
                  variant="light"
                  color="blue"
                  size="sm"
                  leftSection={<IconPencil size={16} />}
                  onClick={openEditCompanyModal}
                >
                  Edit Company
                </Button>
                <Button
                  variant="light"
                  color="blue"
                  size="sm"
                  leftSection={<IconPencil size={16} />}
                  onClick={openEditContactModal}
                >
                  Edit Contact
                </Button>
              </Group>
              {showVerifyDirectlyButton && (
                <Group gap="sm" justify="flex-end">
                  <Button
                    variant="filled"
                    color="primary.6"
                    size="sm"
                    radius="md"
                    leftSection={<IconCheck size={16} />}
                    onClick={openVerifyDirectlyModal}
                    disabled={actionLoading}
                  >
                    Verify Directly
                  </Button>
                </Group>
              )}
            </Flex>
          </Flex>
        </Paper>

        {company?.status === "ACTION_REQUIRED" && company?.adminComments && (
          <Paper p="md" radius="lg" mb="xl" bg="#D84B4B33" style={{ border: "1px solid var(--mantine-color-red-3)" }}>
            <Flex gap="sm" align="flex-start">
              <Box mt={2}>
                <IconAlertCircle size={20} color="#D84B4B" />
              </Box>
              <Flex direction="column">
                <Text fz="sm" fw={600} c="#D84B4B">
                  Verification Rejected
                </Text>
                <Text fz="sm" c="#D84B4B">
                  {company.adminComments}
                </Text>
              </Flex>
            </Flex>
          </Paper>
        )}
        {/* Enable/Disable Banner - Only for verified profiles */}
        {company?.status === "VERIFIED" && (
          <Paper
            p="md"
            radius="lg"
            mb="xl"
            style={{
              border: "1px solid #E5E7EB",
              backgroundColor: "#fff",
            }}
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Badge variant="light" color={company.adminBlocked ? "#D84B4B" : "green"} size="md" mb="xs">
                  {company.adminBlocked ? "Disabled" : "Enabled"}
                </Badge>
                <Text size="sm" c="slate.6">
                  {company.adminBlocked
                    ? "This company is disabled and hidden from the Discover page."
                    : "This company is enable and visible on the Discover page with full platform access."}
                </Text>
              </Box>
              <Button
                size="sm"
                color={company.adminBlocked ? "primary" : "#D84B4B"}
                variant="filled"
                loading={blockLoading}
                onClick={handleToggleBlock}
              >
                {company.adminBlocked ? "Enable" : "Disable"}
              </Button>
            </Flex>
          </Paper>
        )}

        {/* Tabs Section */}
        <Tabs
          variant="none"
          value={activeTab}
          onChange={setActiveTab}
          styles={{
            ...adminCompanyNavTabsRootStyles,
            tabLabel: {
              width: "100%",
              textAlign: "center",
            },
          }}
        >
          <Box w="100%" style={NAV_TABS_FULL_WIDTH_LINE_STYLE}>
            <Tabs.List styles={adminCompanyNavTabsListStyles}>
              <Tabs.Tab
                value="profile"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("profile", "first")}
              >
                Company Profile
              </Tabs.Tab>
              <Tabs.Tab
                value="collaborations"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("collaborations", "middle")}
              >
                Collaborations
              </Tabs.Tab>
              <Tabs.Tab
                value="interests"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("interests", "middle")}
              >
                Interest Requests
              </Tabs.Tab>
              <Tabs.Tab
                value="creditHistory"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("creditHistory", "middle")}
              >
                Credit History
              </Tabs.Tab>
              <Tabs.Tab
                value="signupLegalAcceptance"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("signupLegalAcceptance", "middle")}
                ta="center"
              >
                Signup legal
                 <br /> acceptance
              </Tabs.Tab>
              <Tabs.Tab
                value="companyAuthorityAcceptance"
                px={{ base: "md", sm: "lg" }}
                py="xs"
                fz={NAV_TAB_LABEL_FZ}
                style={getAdminCompanyTabStyle("companyAuthorityAcceptance", "last")}
                ta="center"
              >
                  Company 
               <br/> authority acceptance
              </Tabs.Tab>
            </Tabs.List>
          </Box>

          <Tabs.Panel value="profile" pt="xl">
            {activeTab === "profile" && <MatchedProfileTab company={company} />}
          </Tabs.Panel>

          <Tabs.Panel value="collaborations" pt="xl">
            {activeTab === "collaborations" && <AdminCollaborationsTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="interests" pt="xl">
            {activeTab === "interests" && <AdminInterestsTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="creditHistory" pt="xl">
            {activeTab === "creditHistory" && <AdminCreditHistoryTab companyId={company.id} />}
          </Tabs.Panel>

          <Tabs.Panel value="signupLegalAcceptance" pt="xl">
            {activeTab === "signupLegalAcceptance" && (
              <AdminSignupLegalAcceptanceTab records={company.user?.signupLegalAcceptances ?? []} />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="companyAuthorityAcceptance" pt="xl">
            {activeTab === "companyAuthorityAcceptance" && (
              <AdminCompanyAuthorityAcceptanceTab records={company.authorityAcceptances ?? []} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Container>
      <ApplyVerificationModal
        opened={registryVerifyModalOpened}
        onClose={closeRegistryVerificationModal}
        onApply={handleApplyRegistryVerification}
        isLoading={registryVerifyLoading}
        verificationData={registryVerificationData}
        headquartersCountry={company.headquartersCountry || ""}
        initialRegistrationIdType={company.registrationIdType ?? null}
        initialRegistrationNumber={company.registrationNumber || ""}
        submitError={registryVerificationSubmitError}
        onClearSubmitError={() => setRegistryVerificationSubmitError(null)}
      />

      <VerifyCompanyModal
        opened={verifyModalOpened}
        onClose={closeVerifyModal}
        onConfirm={handleVerify}
        isLoading={actionLoading}
        companyName={company.legalName}
      />
      <VerifyCompanyModal
        opened={verifyDirectlyModalOpened}
        onClose={closeVerifyDirectlyModal}
        onConfirm={handleVerifyDirectly}
        isLoading={actionLoading}
        companyName={company.legalName}
      />

      <RejectCompanyModal
        opened={rejectModalOpened}
        onClose={closeRejectModal}
        onConfirm={handleReject}
        isLoading={actionLoading}
        companyName={company.legalName}
        title="Reject Company Profile"
        description={
          company.legalName
            ? `You are about to reject the profile for "${company.legalName}". Please provide a reason for rejection.`
            : "Please provide a reason for rejecting this company profile."
        }
        confirmLabel="Reject Profile"
      />
      <RejectCompanyModal
        opened={disableModalOpened}
        onClose={closeDisableModal}
        onConfirm={handleConfirmDisable}
        isLoading={blockLoading}
        companyName={company.legalName}
      />
      <Modal
        opened={editCompanyOpened}
        onClose={() => !savingCompanyEdit && setEditCompanyOpened(false)}
        title="Edit company details"
        centered
        size="lg"
      >
        <TextInput
          label="Legal Name"
          value={companyEditForm.legalName}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, legalName: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Trade Name"
          value={companyEditForm.tradeName}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, tradeName: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Headquarters City"
          value={companyEditForm.headquartersCity}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, headquartersCity: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Headquarters Country"
          value={companyEditForm.headquartersCountry}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, headquartersCountry: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Company Email"
          value={companyEditForm.email}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, email: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Company Phone"
          value={companyEditForm.phoneNumber}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, phoneNumber: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Website"
          value={companyEditForm.website}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, website: e.currentTarget.value }))}
          mb="sm"
        />
        <Textarea
          label="Description"
          minRows={3}
          value={companyEditForm.description}
          onChange={(e) => setCompanyEditForm((prev) => ({ ...prev, description: e.currentTarget.value }))}
        />
        <Flex justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={() => setEditCompanyOpened(false)} disabled={savingCompanyEdit}>
            Cancel
          </Button>
          <Button onClick={() => void saveCompanyEdit()} loading={savingCompanyEdit}>
            Save company
          </Button>
        </Flex>
      </Modal>

      <Modal
        opened={editContactOpened}
        onClose={() => !savingContactEdit && setEditContactOpened(false)}
        title="Edit contact details"
        centered
      >
        <TextInput
          label="Contact Name"
          value={contactEditForm.name}
          onChange={(e) => setContactEditForm((prev) => ({ ...prev, name: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Designation"
          value={contactEditForm.designation}
          onChange={(e) => setContactEditForm((prev) => ({ ...prev, designation: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Email"
          type="email"
          value={contactEditForm.email}
          onChange={(e) => setContactEditForm((prev) => ({ ...prev, email: e.currentTarget.value }))}
          mb="sm"
        />
        <TextInput
          label="Phone Number"
          value={contactEditForm.phoneNumber}
          onChange={(e) => setContactEditForm((prev) => ({ ...prev, phoneNumber: e.currentTarget.value }))}
        />
        <Flex justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={() => setEditContactOpened(false)} disabled={savingContactEdit}>
            Cancel
          </Button>
          <Button onClick={() => void saveContactEdit()} loading={savingContactEdit}>
            Save contact
          </Button>
        </Flex>
      </Modal>

      <Modal
        opened={twoFactorOpen}
        onClose={() => !twoFactorBusy && setTwoFactorOpen(false)}
        title="Two-factor verification"
        centered
      >
        <Text size="sm" mb="sm">
          Enter the 6-digit code from your authenticator app to continue.
        </Text>
        <TextInput
          label="2FA code"
          placeholder="123456"
          value={twoFactorOtp}
          onChange={(e) => setTwoFactorOtp(e.currentTarget.value)}
          inputMode="numeric"
        />
        <Flex justify="flex-end" gap="sm" mt="lg">
          <Button variant="default" onClick={() => setTwoFactorOpen(false)} disabled={twoFactorBusy}>
            Cancel
          </Button>
          <Button onClick={() => void confirmTwoFactorForContactEdit()} loading={twoFactorBusy}>
            Verify & update
          </Button>
        </Flex>
      </Modal>
    </Box>
  );
}
